"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ModalContainer = exports.MODAL_ANIMATION_DURATION = void 0;
var _react = _interopRequireWildcard(require("react"));
var _reactTransitionState = require("react-transition-state");
var _reactUseControlledState = _interopRequireDefault(require("@quid/react-use-controlled-state"));
var _Trigger = require("b2b-components/core/components/Modal/Trigger");
var _classnames = _interopRequireDefault(require("classnames"));
var _Modal = require("./Modal");
var _scrollLock = require("./utils/scrollLock");
require("./Modal.scss");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
// It should be the same as $cds-Modal-Animation-Duration in Modal.scss
const MODAL_ANIMATION_DURATION = exports.MODAL_ANIMATION_DURATION = 150;
const ModalContainer = _ref => {
  let {
    content = () => null,
    dataAttributes,
    isDefaultOpen,
    isOpen,
    onCloseModal,
    onOpenModal,
    title = '',
    triggerObject,
    type,
    hideCloseButton,
    preventClose,
    onKeyDown,
    className,
    isTabNavigationEnabled
  } = _ref;
  const modalRef = (0, _react.useRef)(null);
  const firstUpdate = (0, _react.useRef)(true);
  const lastActiveElement = (0, _react.useRef)(null);
  const [{
    status,
    isMounted
  }, toggle] = (0, _reactTransitionState.useTransition)({
    timeout: MODAL_ANIMATION_DURATION,
    mountOnEnter: true,
    unmountOnExit: true,
    preEnter: true
  });
  const onOpenChange = isOpen => {
    isOpen ? onOpenModal?.() : onCloseModal?.();
  };
  const [isStateOpen, setIsStateOpen] = (0, _reactUseControlledState.default)(isDefaultOpen, isOpen, onOpenChange);
  const handleCloseModal = () => {
    (0, _scrollLock.removeScrollLock)();
  };
  const handleOpenModal = () => {
    (0, _scrollLock.addScrollLock)();
    handleFocus();
  };
  (0, _react.useEffect)(() => {
    if (!isStateOpen && !firstUpdate.current) {
      handleCloseModal();
    }
    if (firstUpdate.current) {
      firstUpdate.current = false;
    }
    if (isStateOpen) {
      handleOpenModal();
      return handleCloseModal;
    }
  }, [isStateOpen]);
  const handleFocus = () => {
    // if cancel button is visible - focus
    const cancelButton = modalRef.current?.querySelector('#modal-cancel-button');
    if (cancelButton) {
      const rect = cancelButton.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom >= 0) {
        cancelButton?.focus();
      }
    }
  };
  const handleKeyDown = event => {
    onKeyDown?.(event);
    if (preventClose) {
      return;
    }
    if (event.key === 'Tab' && isTabNavigationEnabled && modalRef?.current) {
      event.preventDefault();
      event.stopPropagation();
      const focusableSelectors = ['button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])', 'textarea:not([disabled])', 'a[href]', '[tabindex]:not([tabindex="-1"])'].join(',');
      const allElements = Array.from(modalRef.current.querySelectorAll(focusableSelectors));
      const visibleElements = allElements.filter(el => {
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden';
      });
      const elements = [...visibleElements].sort((a, b) => {
        if (a.tabIndex > 0 && b.tabIndex > 0) {
          return a.tabIndex - b.tabIndex;
        }
        if (a.tabIndex > 0 && b.tabIndex <= 0) {
          return -1;
        }
        if (a.tabIndex <= 0 && b.tabIndex > 0) {
          return 1;
        }
        return 0;
      });
      if (!elements.length) {
        return;
      }
      const current = document.activeElement;
      const currentIndex = elements.findIndex(el => el === current);
      const delta = event.shiftKey ? -1 : 1;
      let nextIndex;
      if (currentIndex === -1) {
        nextIndex = delta > 0 ? 0 : elements.length - 1;
      } else {
        nextIndex = (currentIndex + delta + elements.length) % elements.length;
      }
      elements[nextIndex].focus();
    }
    if (event.key === 'Escape' || event.key === 'Esc') {
      setIsStateOpen(false);
    }
  };
  const handleClickAway = event => {
    if (preventClose) {
      return;
    }
    if (modalRef.current?.contains(event.target)) {
      return;
    }
    setIsStateOpen(false);
  };
  const handleClose = event => {
    if (preventClose) {
      return;
    }
    event?.preventDefault();
    setIsStateOpen(false);
  };
  const handleOpen = event => {
    event?.preventDefault();
    setIsStateOpen(true);
  };
  (0, _react.useEffect)(() => {
    if (isStateOpen !== undefined) {
      toggle(!!isStateOpen); // it's important to cast it to boolean

      if (isTabNavigationEnabled) {
        lastActiveElement.current = document.activeElement;
        const requestId = requestAnimationFrame(() => {
          if (modalRef?.current) {
            const input = modalRef.current.querySelector('input');
            if (input && input instanceof HTMLElement) {
              input.focus();
            }
          }
        });
        document.addEventListener('keydown', handleKeyDown);
        return () => {
          document.removeEventListener('keydown', handleKeyDown);
          cancelAnimationFrame(requestId);
          lastActiveElement.current?.focus();
        };
      }
    }
  }, [isStateOpen, toggle]);
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_Trigger.Trigger, {
    onOpen: handleOpen,
    triggerObject: triggerObject
  }), isMounted && /*#__PURE__*/_react.default.createElement(_Modal.Modal, {
    hideCloseButton: hideCloseButton,
    content: content(handleClose),
    dataAttributes: dataAttributes,
    modalRef: modalRef,
    onClickAway: handleClickAway,
    onClose: handleClose,
    onKeyDown: handleKeyDown,
    title: title,
    type: type,
    className: className,
    transitionClassName: (0, _classnames.default)({
      ['cds-Modal-Animation--FadeIn']: ['entering', 'preEnter'].includes(status),
      ['cds-Modal-Animation--FadeOut']: status === 'exiting'
    })
  }));
};
exports.ModalContainer = ModalContainer;
//# sourceMappingURL=ModalContainer.js.map